import type { IRowNode } from "ag-grid-community";

/**
 * Column the grid focuses when a BAG object row is selected.
 */
export const FOCUS_COLUMN = "addressFormatted";

export interface IRowFocusGridApi {
  /**
   * The subset of the ag-grid API that RowFocusTracker needs.
   * Kept minimal so it can be faked in unit tests.
   */
  getRowNode(id: string): IRowNode | undefined;
  setRowNodeExpanded(
    node: IRowNode,
    expanded: boolean,
    expandParents?: boolean,
    forceSync?: boolean,
  ): void;
  ensureNodeVisible(node: IRowNode, position: "top"): void;
  setFocusedCell(rowIndex: number, colKey: string): void;
}

export class RowFocusTracker {
  /**
   * Keeps at most one master row expanded, in O(1) per selection.
   *
   * The grid shows the detail view of a single BAG object at a time. Rather
   * than walking every row node to collapse the others on each click (which
   * scales with the size of the lead search), we remember the id of the row we
   * expanded and collapse just that one when focus moves elsewhere.
   *
   * Rows can also be expanded outside of `focus` (group-cell chevron, Enter
   * key). ag-grid reports those through `rowGroupOpened`; feed that event to
   * `onRowGroupOpened` so the tracker stays in sync.
   */
  private expandedRowId: string | null = null;

  focus(
    api: IRowFocusGridApi,
    rowId: string,
    ensureNodeVisible: boolean,
  ): boolean {
    /**
     * Expand the given row (collapsing the previously expanded one), optionally
     * scroll it into view, and focus its address cell.
     * Returns false if the row is not in the grid.
     */
    const rowNode = api.getRowNode(rowId);
    if (!rowNode) {
      return false;
    }
    this.collapseTrackedRow(api, rowId);
    this.expandedRowId = rowId;
    if (!rowNode.expanded) {
      api.setRowNodeExpanded(rowNode, true, false, true);
    }
    if (ensureNodeVisible) {
      api.ensureNodeVisible(rowNode, "top");
    }
    api.setFocusedCell(rowNode.rowIndex!, FOCUS_COLUMN);
    return true;
  }

  onRowGroupOpened(
    api: IRowFocusGridApi,
    node: IRowNode,
    expanded: boolean,
  ): void {
    /**
     * Keep the tracker in sync with expansions that bypass `focus`.
     */
    const rowId = node.id ?? null;
    if (!expanded) {
      if (rowId !== null && rowId === this.expandedRowId) {
        this.expandedRowId = null;
      }
      return;
    }
    if (rowId === null || rowId === this.expandedRowId) {
      return;
    }
    this.collapseTrackedRow(api, rowId);
    this.expandedRowId = rowId;
  }

  private collapseTrackedRow(api: IRowFocusGridApi, keepRowId: string): void {
    /**
     * Collapse the currently tracked row, unless it is the row being kept.
     */
    if (this.expandedRowId === null || this.expandedRowId === keepRowId) {
      return;
    }
    const previous = api.getRowNode(this.expandedRowId);
    if (previous?.expanded) {
      api.setRowNodeExpanded(previous, false, false, false);
    }
    this.expandedRowId = null;
  }
}
